import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, withLatestFrom, map } from 'rxjs/operators';

import {
  CalendarsList,
  CalendarsListSuccess,
  ECalendarsActions,
  GetBaseCalendar,
  GetBaseCalendarSuccess,
  GetUserCalendar,
  GetUserCalendarSuccess,
  SetSelectedUser,
  UpdateBaseCalendarUser,
  UpdateBaseCalendarUserSuccess,
  AddWorkingTimeSchema,
  UpdateCustomCalendar,
  UpdateCustomCalendarSuccess,
  ModifyWorkingTimeSchema,
  ResetWorkingTimeSchema,
  SetInitialBaseCalendarUser,
  ChangesOverWorkingTimeSchemaSuccess,
  SetInitialCustomCalendarUser
} from '../actions/calendars.actions';
import { WorkingTimeSchemaService } from '../../working-time-schema.service';
import { BzgToastService } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { getAllUserCalendars, getSelectedUser, getCalendarDic, getCustomToDelete } from '../selectors/calendars.selectors';
import { select, Store } from '@ngrx/store';
import { CalendarsOperations } from '../reducers/calendars-operation';
import { IAppState } from 'app/IAppState';

@Injectable()
export class calendarsEffects {
  getCalendarsOptions$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CalendarsList>(ECalendarsActions.CalendarsList),
      switchMap(() => this._workingTimeSchemaService.getCalendarOptions()),
      switchMap((options: IBaseCalendar[]) =>
        of(new CalendarsListSuccess(options))
      )
    )
  );
  getBaseCalendar$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetBaseCalendar>(ECalendarsActions.GetBaseCalendar),
      withLatestFrom(this._store.pipe(select(getSelectedUser))),
      map(([action, user]) => user),
      switchMap((user: ISpecificUser) =>
        this._workingTimeSchemaService.getBaseCalendar(user.idUser)
      ),
      switchMap((baseCalendar: IBaseCalendar) => [
        new GetBaseCalendarSuccess(baseCalendar),
        new SetInitialBaseCalendarUser(baseCalendar)
      ])
    )
  );
  getUserCalendar$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetUserCalendar>(ECalendarsActions.GetUserCalendar),
      withLatestFrom(this._store.pipe(select(getSelectedUser))),
      map(([action, user]) => user),
      switchMap(user =>
        this._workingTimeSchemaService.getWorkingTimeSchema(user.idUser)
      ),
      switchMap((list: IWorkingTimeSchema[]) => [
        new GetUserCalendarSuccess(list),
        new SetInitialCustomCalendarUser(list)
      ])
    )
  );
  setSelectedUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSelectedUser>(ECalendarsActions.SetSelectedUser),
      switchMap(param => of(new GetBaseCalendar(param.payload.idUser.toString())))
    )
  );
  updateBaseCalendarByUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateBaseCalendarUser>(ECalendarsActions.UpdateBaseCalendarUser),
      switchMap(param =>
        this._workingTimeSchemaService.updateCalendarBaseByUser(
          param.idUser,
          param.idWorkingTimeSchema
        )
      ),
      switchMap(() => of(new UpdateBaseCalendarUserSuccess()))
    )
  );
  updateCustomCalendar$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateCustomCalendar>(ECalendarsActions.UpdateCustomCalendar),
      withLatestFrom(this._store.pipe(select(getAllUserCalendars))),
      withLatestFrom(this._store.pipe(select(getCustomToDelete))),
      map(([[action, customCalendars], toDelete]) => ({
        customCalendars,
        toDelete
      })),
      switchMap(payload => {
        const updatedCalendars = payload.customCalendars.map(item => {
          const isToDelete = !!Object.keys(payload.toDelete).find(
            id => id === `${item.idWorkingTimeData}`
          );
          const newItem = {
            ...item,
            idWorkingTimeData: isToDelete ? -1 : item.idWorkingTimeData
          };
          return newItem;
        });
        return this._workingTimeSchemaService.updateCustomCalendar(
          updatedCalendars,
          payload.toDelete
        );
      }),
      switchMap(() => {
        this.showSuccessToast();
        return of(new UpdateCustomCalendarSuccess());
      })
    )
  );
  refreshUserCalendar$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateCustomCalendarSuccess>(
        ECalendarsActions.UpdateCustomCalendarSuccess
      ),
      switchMap(() => of(new GetUserCalendar()))
    )
  );
  workingTimeSchemaOperations$ = createEffect(() =>
    this._actions$.pipe(
      ofType<
        AddWorkingTimeSchema | ModifyWorkingTimeSchema | ResetWorkingTimeSchema
      >(
        ECalendarsActions.AddWorkingTimeSchema,
        ECalendarsActions.ModifyWorkingTimeSchema,
        ECalendarsActions.ResetWorkingTimeSchema
      ),
      withLatestFrom(this._store.pipe(select(getAllUserCalendars))),
      map(([action, workingTimeSchema]) => ({
        workkingTimeSchema: workingTimeSchema,
        newSchema: action.workingTimeSchema,
        operation: action.operation,
        actionType: action.type
      })),
      withLatestFrom(this._store.pipe(select(getCalendarDic))),
      withLatestFrom(this._store.pipe(select(getSelectedUser))),
      map(([[payload, calendarsDict], user]) => ({
        ...payload,
        calendarsDict,
        idUser: user.idUser
      })),
      switchMap(payload =>
        CalendarsOperations.workingTimeSchemaOperationsWorker(
          payload.idUser,
          payload.workkingTimeSchema,
          payload.newSchema,
          payload.calendarsDict,
          payload.operation
        )
      ),
      switchMap((workingTimeSchemaUpdated: IWorkingTimeSchema[]) =>
        of(new ChangesOverWorkingTimeSchemaSuccess(workingTimeSchemaUpdated))
      )
    )
  );

  constructor(
    private _workingTimeSchemaService: WorkingTimeSchemaService,
    private _actions$: Actions,
    private _toastService: BzgToastService,
    private _translateService: TranslateService,
    private _store: Store<IAppState>
  ) {
  }

  private showSuccessToast() {
    this._translateService
      .get('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY')
      .subscribe((message: string) => {
        this._toastService.open(message, {
          type: 'success',
          duration: 5000,
          verticalPosition: 'top',
          horizontalPosition: 'right'
        });
      });
  }
}
